import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Paper, TextField, Button, Grid, CircularProgress } from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';

const EnterpriseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [adminInfo, setAdminInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '', rcs: '', tva: '', address: '', city: '', country: '',
    admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '',
    job_title: '', department: '', entity: '', mobile: ''
  });

  useEffect(() => {
    if (isEdit) {
      Promise.all([
        api.get(`/api/v1/enterprises/${id}`),
        api.get('/api/v1/users/')
      ]).then(([entRes, usersRes]) => {
        const e = entRes.data;
        // Find the admin user for this enterprise
        const admin = usersRes.data.find(u => u.enterprise_id === e.id && u.role === 'enterprise_admin');
        setAdminInfo(admin || null);
        setFormData({
          name: e.name || '', rcs: e.rcs || '', tva: e.tva || '', address: e.address || '', city: e.city || '', country: e.country || '',
          admin_email: admin?.email || '',
          admin_password: '',
          admin_first_name: admin?.first_name || '',
          admin_last_name: admin?.last_name || '',
          job_title: admin?.job_title || '',
          department: admin?.department || '',
          entity: admin?.entity || '',
          mobile: admin?.mobile || ''
        });
        console.log('Enterprise loaded:', e, 'Admin:', admin);
      }).catch(() => toast.error('Failed to load enterprise')).finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Enterprise name is required'); return; }
    if (!isEdit && !formData.admin_email.trim()) { toast.error('Admin email is required'); return; }
    // Admin password is optional - will be auto-generated if empty
    
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/v1/enterprises/${id}`, {
          name: formData.name, rcs: formData.rcs, tva: formData.tva,
          address: formData.address, city: formData.city, country: formData.country
        });
        if (adminInfo) {
          const adminUpdate = {
            email: formData.admin_email, first_name: formData.admin_first_name,
            last_name: formData.admin_last_name, job_title: formData.job_title,
            department: formData.department, entity: formData.entity, mobile: formData.mobile
          };
          if (formData.admin_password) adminUpdate.password = formData.admin_password;
          await api.patch(`/api/v1/users/${adminInfo.id}`, adminUpdate);
        }
      } else {
        await api.post('/api/v1/enterprises/', {
          name: formData.name, rcs: formData.rcs, tva: formData.tva,
          address: formData.address, city: formData.city, country: formData.country,
          admin_email: formData.admin_email, admin_password: formData.admin_password,
          admin_first_name: formData.admin_first_name, admin_last_name: formData.admin_last_name,
          job_title: formData.job_title, department: formData.department,
          entity: formData.entity, mobile: formData.mobile
        });
      }
      toast.success(isEdit ? 'Enterprise updated' : 'Enterprise created');
      navigate('/enterprises');
      localStorage.setItem("entNeedRefresh", "true");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button onClick={() => navigate('/enterprises')} startIcon={<ArrowBack />} variant="outlined" size="small">Back</Button>
        <Typography variant="h5" fontWeight={600} color="#2c3e50">{isEdit ? '✏️ Edit Enterprise' : '🆕 Create New Enterprise'}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1abc9c' }}>📋 Enterprise Information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField fullWidth label="Enterprise Name *" value={formData.name} onChange={e => handleChange('name', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="RCS" value={formData.rcs} onChange={e => handleChange('rcs', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="TVA" value={formData.tva} onChange={e => handleChange('tva', e.target.value)} size="small" /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Address" value={formData.address} onChange={e => handleChange('address', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="City" value={formData.city} onChange={e => handleChange('city', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Country" value={formData.country} onChange={e => handleChange('country', e.target.value)} size="small" /></Grid>
        </Grid>
      </Paper>

      {/* Admin Account - shown for both create and edit */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1abc9c' }}>
          👤 Admin Account {isEdit && '(leave password empty to keep current)'}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="First Name *" value={formData.admin_first_name} onChange={e => handleChange('admin_first_name', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name *" value={formData.admin_last_name} onChange={e => handleChange('admin_last_name', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Email *" type="email" value={formData.admin_email} onChange={e => handleChange('admin_email', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={isEdit ? 'New Password (leave empty to keep)' : 'Password (leave empty for auto-generated)'} type="password" value={formData.admin_password} onChange={e => handleChange('admin_password', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Job Title" value={formData.job_title} onChange={e => handleChange('job_title', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Department" value={formData.department} onChange={e => handleChange('department', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Entity" value={formData.entity} onChange={e => handleChange('entity', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Mobile" value={formData.mobile} onChange={e => handleChange('mobile', e.target.value)} size="small" /></Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={() => navigate('/enterprises')} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <Save />} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
          {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Enterprise'}
        </Button>
      </Box>
    </Box>
  );
};

export default EnterpriseForm;
